import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import apiClient from "../../lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Eye, Trash, Plus, CaretLeft, Trophy, XCircle,
  CheckFat, Percent, Hourglass, Fire, Lightning, X, PencilSimple,
  ArrowsClockwise, Robot,
} from "@phosphor-icons/react";

export default function AdminPicksManage() {
  const [picks, setPicks]         = useState([]);
  const [stats, setStats]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState("all");
  // Quick-result state: { pickId, type: "won"|"lost" }
  const [quickResult, setQuickResult] = useState(null);
  // Flash modal state: pickId or null
  const [flashModal, setFlashModal] = useState(null);
  // Edit modal state: pick object or null
  const [editPick, setEditPick]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [picksRes, statsRes] = await Promise.all([
        apiClient.get("/admin/picks"),
        apiClient.get("/public/winrate"),
      ]);
      setPicks(picksRes.data.picks);
      setStats(statsRes.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const [settling, setSettling] = useState(false);
  const [settleResult, setSettleResult] = useState(null); // resumen del último auto-settle
  const [pendingDelete, setPendingDelete] = useState(null); // pick object para modal de confirmación
  // Selección múltiple para cierre en lote
  const [selected, setSelected] = useState(new Set());
  const [showBatchModal, setShowBatchModal] = useState(false);

  const counts = {
    all: picks.length,
    pending: picks.filter(p => p.status === "pending").length,
    live: picks.filter(p => p.status === "live").length,
    won: picks.filter(p => p.status === "won").length,
    lost: picks.filter(p => p.status === "lost").length,
    void: picks.filter(p => p.status === "void").length,
  };

  const runAutoSettle = async () => {
    setSettling(true);
    setSettleResult(null);
    try {
      const res = await apiClient.post("/admin/picks/auto-settle");
      const d = res.data;
      setSettleResult(d);
      const won = (d.settled || []).filter(x => x.result === "won").length;
      const lost = (d.settled || []).filter(x => x.result === "lost").length;
      if (d.settled_count > 0) {
        toast.success(`Liquidadas ${d.settled_count}: ${won} ganadas, ${lost} perdidas`);
      } else {
        toast.info("Sin resultados nuevos todavía. Las pendientes sin marcador final quedan igual.");
      }
      load();
    } catch (e) {
      toast.error("No se pudo sincronizar. Intenta de nuevo.");
    } finally {
      setSettling(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const ids = filtered.filter(p => canSetResult(p.status)).map(p => p.id);
    const allSelected = ids.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };

  const del = async (id) => {
    // Abre modal de confirmación (reemplaza el doble-tap)
    const pick = picks.find(p => p.id === id);
    setPendingDelete(pick || { id });
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    setPendingDelete(null);
    try {
      await apiClient.delete(`/admin/picks/${id}`);
      toast.success("Análisis eliminado");
      setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
      load();
    } catch { toast.error("Error al eliminar"); }
  };

  const activateFlash = async (pickId, minutes) => {
    try {
      await apiClient.post(`/admin/picks/${pickId}/flash`, { duration_minutes: minutes });
      toast.success(`⚡ Flash Pick activado por ${minutes} minutos`);
      setFlashModal(null);
      load();
    } catch { toast.error("Error al activar Flash Pick"); }
  };

  const cancelFlash = async (pickId) => {
    try {
      await apiClient.delete(`/admin/picks/${pickId}/flash`);
      toast.success("Flash Pick cancelado");
      load();
    } catch { toast.error("Error al cancelar Flash Pick"); }
  };

  const filtered = picks.filter(p => filter === "all" || p.status === filter);
  const canSetResult = (s) => s === "pending" || s === "live";

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-6 pb-16">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-white mb-4 uppercase tracking-wider" data-testid="admin-manage-back">
        <CaretLeft className="w-3.5 h-3.5" /> Panel
      </Link>

      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] text-yellow-400 tracking-[0.25em] uppercase font-bold mb-2">Gestión</div>
          <h1 className="text-display font-black text-3xl lg:text-4xl tracking-tighter leading-none">Análisis publicados</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={runAutoSettle}
            disabled={settling}
            data-testid="admin-auto-settle"
            title="Busca los resultados finales y marca ganadas/perdidas automáticamente"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-all disabled:opacity-50"
            style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.18), rgba(16,185,129,0.08))", border: "1px solid rgba(16,185,129,0.4)", color: "#6EE7B7" }}
          >
            <ArrowsClockwise weight="bold" className={`w-4 h-4 ${settling ? "animate-spin" : ""}`} />
            {settling ? "Sincronizando…" : "Sincronizar resultados"}
          </button>
          <Link to="/admin/analisis/nuevo" className="btn-primary" data-testid="admin-manage-create">
            <Plus weight="bold" className="w-4 h-4" /> Nuevo análisis
          </Link>
        </div>
      </div>

      {/* ── Resumen de auto-liquidación ────────────────────────────── */}
      {settleResult && (
        <div className="rounded-xl p-4 mb-6 text-sm" style={{ background: "rgba(16,185,129,0.07)", border: "1px solid rgba(16,185,129,0.25)" }}>
          <div className="flex items-center gap-2 font-bold text-emerald-300 mb-2">
            <Robot weight="fill" className="w-4 h-4" /> Liquidación automática
          </div>
          {settleResult.settled_count > 0 ? (
            <div className="space-y-1 text-zinc-300">
              {(settleResult.settled || []).slice(0, 8).map((x) => (
                <div key={x.id} className="flex items-center justify-between gap-2">
                  <span className="truncate">{x.match}</span>
                  <span className={`font-black flex-shrink-0 ${x.result === "won" ? "text-emerald-400" : x.result === "lost" ? "text-red-400" : "text-zinc-400"}`}>
                    {x.result === "won" ? "GANADA" : x.result === "lost" ? "PERDIDA" : "NULA"} · {x.score}
                  </span>
                </div>
              ))}
              {settleResult.settled_count > 8 && <div className="text-zinc-500">…y {settleResult.settled_count - 8} más</div>}
            </div>
          ) : (
            <p className="text-zinc-400">No había partidos finalizados con resultado disponible. El sistema revisa solo cada 15 minutos.</p>
          )}
          {settleResult.skipped_count > 0 && (
            <p className="text-zinc-500 mt-2 text-xs">{settleResult.skipped_count} señal(es) quedaron pendientes: aún sin marcador final o con mercado que requiere revisión manual.</p>
          )}
        </div>
      )}

      {/* ── Win Rate Stats Bar ───────────────────────────────────── */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="admin-stats-bar">
          <StatCard
            icon={<Trophy weight="fill" className="w-5 h-5 text-emerald-400" />}
            label="Ganadas"
            value={stats.won}
            color="text-emerald-400"
            testid="stats-won"
          />
          <StatCard
            icon={<XCircle weight="fill" className="w-5 h-5 text-red-400" />}
            label="Perdidas"
            value={stats.lost}
            color="text-red-400"
            testid="stats-lost"
          />
          <StatCard
            icon={<Percent weight="bold" className="w-5 h-5 text-yellow-400" />}
            label="Win Rate"
            value={`${stats.win_rate}%`}
            color="text-yellow-400"
            sub={`${stats.settled} resueltos`}
            testid="stats-winrate"
          />
          <StatCard
            icon={
              stats.streak_type === "won"
                ? <Fire weight="fill" className="w-5 h-5 text-orange-400" />
                : <Hourglass weight="fill" className="w-5 h-5 text-zinc-400" />
            }
            label={stats.streak_type === "won" ? "Racha actual" : "Sin racha"}
            value={stats.streak > 0 ? `${stats.streak} seguidas` : "—"}
            color={stats.streak_type === "won" ? "text-orange-400" : "text-zinc-400"}
            sub={stats.streak_type === "won" ? "consecutivas ganadas" : "últimas perdidas"}
            testid="stats-streak"
          />
        </div>
      )}

      {/* ── Filters ─────────────────────────────────────────────── */}
      <div className="surface rounded-lg p-2 mb-6 inline-flex gap-1 flex-wrap">
        {[
          { k: "all",     l: "Todos" },
          { k: "pending", l: "Pendientes" },
          { k: "live",    l: "En juego" },
          { k: "won",     l: "Ganadas" },
          { k: "lost",    l: "Perdidas" },
          { k: "void",    l: "Anuladas" },
        ].map(f => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`admin-manage-filter-${f.k}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded transition-colors ${
              filter === f.k ? "bg-yellow-400 text-black" : "text-zinc-400 hover:text-white"
            }`}
          >
            {f.l} <span className={filter === f.k ? "opacity-70" : "text-zinc-600"}>({counts[f.k] ?? 0})</span>
          </button>
        ))}
      </div>

      {/* ── Barra de acciones en lote ─────────────────────────── */}
      {selected.size > 0 && (
        <div className="flex items-center justify-between gap-3 mb-4 px-4 py-3 rounded-xl border"
          style={{ borderColor: "var(--gold)", background: "rgba(212,175,55,0.06)" }}
          data-testid="batch-bar">
          <div className="text-sm font-bold text-white">
            {selected.size} seleccionada{selected.size !== 1 ? "s" : ""}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelected(new Set())}
              className="text-xs text-zinc-400 hover:text-white px-3 py-2 rounded-lg transition-colors">
              Limpiar
            </button>
            <button onClick={() => setShowBatchModal(true)} data-testid="batch-close-btn"
              className="flex items-center gap-2 text-xs font-black px-4 py-2.5 rounded-lg transition-all"
              style={{ background: "var(--gold)", color: "#000" }}>
              <CheckFat weight="fill" className="w-4 h-4" />
              Cerrar seleccionadas
            </button>
          </div>
        </div>
      )}

      {/* ── Content ─────────────────────────────────────────────── */}
      {loading ? (
        <div className="surface rounded-xl h-96 animate-pulse" />
      ) : filtered.length === 0 ? (
        <div className="surface rounded-xl px-5 py-16 text-center">
          <div className="text-display font-bold text-base mb-1">Sin análisis en esta vista</div>
          <div className="text-xs text-zinc-500">Los análisis aparecerán aquí al publicarlos.</div>
        </div>
      ) : (
        <>
          {/* ── Mobile Cards (< lg) ─────────────────────────────── */}
          <div className="lg:hidden space-y-3">
            {filtered.map(p => (
              <MobilePickCard
                key={p.id}
                p={p}
                quickResult={quickResult}
                canSetResult={canSetResult}
                del={del}
                setEditPick={setEditPick}
                setQuickResult={setQuickResult}
                setFlashModal={setFlashModal}
                cancelFlash={cancelFlash}
                load={load}
                selected={selected}
                toggleSelect={toggleSelect}
              />
            ))}
          </div>

          {/* ── Desktop Table (lg+) ──────────────────────────────── */}
          <div className="hidden lg:block surface rounded-xl overflow-hidden">
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full min-w-[820px] text-sm">
                <thead className="bg-white/[0.02]">
                  <tr className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold">
                    <th className="px-3 py-3 w-10">
                      <input type="checkbox" data-testid="batch-select-all"
                        checked={filtered.filter(p => canSetResult(p.status)).length > 0 &&
                          filtered.filter(p => canSetResult(p.status)).every(p => selected.has(p.id))}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded accent-yellow-500 cursor-pointer" />
                    </th>
                    <th className="text-left px-5 py-3 font-semibold">Partido</th>
                    <th className="text-left px-3 py-3 font-semibold">Pick</th>
                    <th className="text-right px-3 py-3 font-semibold">Cuota</th>
                    <th className="text-center px-3 py-3 font-semibold">Estado</th>
                    <th className="text-right px-3 py-3 font-semibold">Fecha</th>
                    <th className="text-right px-5 py-3 font-semibold">Resultado</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(p => (
                    <tr key={p.id} className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                      <td className="px-3 py-4">
                        {canSetResult(p.status) && (
                          <input type="checkbox" data-testid={`batch-select-${p.id}`}
                            checked={selected.has(p.id)}
                            onChange={() => toggleSelect(p.id)}
                            className="w-4 h-4 rounded accent-yellow-500 cursor-pointer" />
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <div className="font-semibold truncate max-w-[220px]">{p.home_team} vs {p.away_team}</div>
                        <div className="text-xs text-zinc-500 uppercase tracking-wider mt-0.5">{p.sport} · {p.league}</div>
                      </td>
                      <td className="px-3 py-4 text-zinc-300 max-w-[160px]">
                        <div className="truncate">{p.pick_selection}</div>
                        <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">{p.market}</div>
                      </td>
                      <td className="px-3 py-4 text-right font-mono font-bold text-yellow-400">{p.odds?.toFixed(2)}</td>
                      <td className="px-3 py-4 text-center"><StatusBadge status={p.status} /></td>
                      <td className="px-3 py-4 text-right text-xs text-zinc-500 font-mono">
                        {p.match_date ? format(new Date(p.match_date), "dd MMM HH:mm") : "—"}
                      </td>
                      <td className="px-5 py-4 text-right">
                        {canSetResult(p.status) ? (
                          quickResult?.pickId === p.id ? (
                            <QuickResultRow
                              pick={p}
                              type={quickResult.type}
                              onCancel={() => setQuickResult(null)}
                              onSaved={() => { setQuickResult(null); load(); }}
                            />
                          ) : (
                            <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
                              {p.is_flash ? (
                                <button onClick={() => cancelFlash(p.id)} data-testid={`flash-cancel-${p.id}`}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-all bg-orange-500/20 text-orange-400 border border-orange-500/40 hover:bg-orange-500/30">
                                  <Lightning weight="fill" className="w-3.5 h-3.5" /> FLASH ACTIVO
                                </button>
                              ) : (
                                <button onClick={() => setFlashModal(p.id)} data-testid={`flash-btn-${p.id}`}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-all bg-orange-500/10 text-orange-400 border border-orange-500/25 hover:bg-orange-500/20">
                                  <Lightning weight="fill" className="w-3.5 h-3.5" /> FLASH
                                </button>
                              )}
                              <button onClick={() => setQuickResult({ pickId: p.id, type: "won" })} data-testid={`quick-won-${p.id}`}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-all bg-emerald-500/10 text-emerald-400 border border-emerald-500/25 hover:bg-emerald-500/20">
                                <CheckFat weight="fill" className="w-3.5 h-3.5" /> GANAMOS
                              </button>
                              <button onClick={() => setQuickResult({ pickId: p.id, type: "lost" })} data-testid={`quick-lost-${p.id}`}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-all bg-red-500/10 text-red-400 border border-red-500/25 hover:bg-red-500/20">
                                <XCircle weight="fill" className="w-3.5 h-3.5" /> PERDIMOS
                              </button>
                              <Link to={`/jugadas/${p.id}`} className="btn-ghost p-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid={`admin-manage-view-${p.id}`}>
                                <Eye className="w-4 h-4" />
                              </Link>
                              <button onClick={() => setEditPick(p)} className="btn-ghost p-1.5 rounded-lg hover:text-yellow-400 hover:bg-yellow-400/10 transition-colors" data-testid={`admin-manage-edit-${p.id}`}>
                                <PencilSimple className="w-4 h-4" />
                              </button>
                              <button onClick={() => del(p.id)} data-testid={`admin-manage-delete-${p.id}`}
                                className="btn-ghost p-1.5 rounded-lg transition-all hover:text-red-400 hover:bg-red-400/10">
                                <Trash className="w-4 h-4" />
                              </button>
                            </div>
                          )
                        ) : (
                          <div className="inline-flex items-center gap-2">
                            {p.result_note && <span className="text-xs text-zinc-400 font-mono">{p.result_note}</span>}
                            <Link to={`/jugadas/${p.id}`} className="btn-ghost p-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid={`admin-manage-view-${p.id}`}>
                              <Eye className="w-4 h-4" />
                            </Link>
                            <button onClick={() => setEditPick(p)} className="btn-ghost p-1.5 rounded-lg hover:text-yellow-400 hover:bg-yellow-400/10 transition-colors" data-testid={`admin-manage-edit-${p.id}`}>
                              <PencilSimple className="w-4 h-4" />
                            </button>
                            <button onClick={() => del(p.id)} data-testid={`admin-manage-delete-${p.id}`}
                              className="btn-ghost p-1.5 rounded-lg transition-all hover:text-red-400 hover:bg-red-400/10">
                              <Trash className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Flash Pick Modal */}
      {flashModal && (
        <FlashModal
          pick={picks.find(p => p.id === flashModal)}
          onConfirm={(mins) => activateFlash(flashModal, mins)}
          onClose={() => setFlashModal(null)}
        />
      )}

      {/* Edit Pick Modal */}
      {editPick && (
        <EditPickModal
          pick={editPick}
          onClose={() => setEditPick(null)}
          onSaved={() => { setEditPick(null); load(); }}
        />
      )}

      {/* ── Modal confirmación de borrado ─────────────────────── */}
      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}>
          <div className="surface rounded-2xl p-6 max-w-sm w-full border border-red-500/25 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-500/15 flex items-center justify-center flex-shrink-0">
                <Trash weight="fill" className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <div className="font-bold text-white mb-1">¿Eliminar análisis?</div>
                <div className="text-sm text-zinc-400">
                  {pendingDelete.home_team} vs {pendingDelete.away_team}
                  <br />Esta acción no se puede deshacer.
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setPendingDelete(null)}
                className="flex-1 py-2.5 rounded-lg border border-white/10 text-zinc-400 hover:text-white hover:border-white/20 transition-all text-sm font-semibold"
                data-testid="delete-cancel-btn">
                Cancelar
              </button>
              <button onClick={confirmDelete}
                className="flex-1 py-2.5 rounded-lg bg-red-500/20 border border-red-500/30 text-red-300 hover:bg-red-500/30 transition-all text-sm font-bold"
                data-testid="delete-confirm-btn">
                Sí, eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal cierre en lote ──────────────────────────────── */}
      {showBatchModal && (
        <BatchCloseModal
          picks={picks.filter(p => selected.has(p.id))}
          onClose={() => setShowBatchModal(false)}
          onDone={() => { setShowBatchModal(false); setSelected(new Set()); load(); }}
        />
      )}
    </div>
  );
}

/* ── Modal de cierre en lote ─────────────────────────────────── */
function BatchCloseModal({ picks, onClose, onDone }) {
  const [type, setType] = useState("won");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(0);

  const saveAll = async () => {
    setSaving(true);
    let ok = 0;
    for (const p of picks) {
      try {
        await apiClient.post(`/admin/picks/${p.id}/result`, { status: type, result_note: note });
        ok++;
      } catch { /* continúa con el siguiente */ }
      setDone(ok);
    }
    toast.success(`${ok} de ${picks.length} cerradas como ${type === "won" ? "ganadas" : "perdidas"}`);
    setSaving(false);
    onDone();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}>
      <div className="surface rounded-2xl p-6 max-w-md w-full border shadow-2xl" style={{ borderColor: "var(--gold)" }}>
        <div className="font-bold text-white text-lg mb-1">Cerrar {picks.length} seleccionadas</div>
        <div className="text-sm text-zinc-400 mb-5">Se aplicará el mismo resultado a todas.</div>
        <div className="grid grid-cols-2 gap-2 mb-4">
          <button onClick={() => setType("won")} data-testid="batch-type-won"
            className={`py-3 rounded-xl font-black text-sm transition-all ${type === "won" ? "bg-emerald-500/20 border-2 border-emerald-500/60 text-emerald-300" : "bg-white/[0.04] border-2 border-white/[0.08] text-zinc-500"}`}>
            GANAMOS
          </button>
          <button onClick={() => setType("lost")} data-testid="batch-type-lost"
            className={`py-3 rounded-xl font-black text-sm transition-all ${type === "lost" ? "bg-red-500/20 border-2 border-red-500/60 text-red-300" : "bg-white/[0.04] border-2 border-white/[0.08] text-zinc-500"}`}>
            PERDIMOS
          </button>
        </div>
        <input value={note} onChange={e => setNote(e.target.value)}
          placeholder="Nota / marcador (opcional, ej: 2-1)"
          data-testid="batch-note"
          className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none mb-5" />
        {saving && (
          <div className="mb-4">
            <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${(done / picks.length) * 100}%`, background: "var(--gold)" }} />
            </div>
            <div className="text-xs text-zinc-500 mt-1.5 text-center">{done} de {picks.length}</div>
          </div>
        )}
        <div className="flex gap-3">
          <button onClick={onClose} disabled={saving}
            className="flex-1 py-2.5 rounded-lg border border-white/10 text-zinc-400 hover:text-white transition-all text-sm font-semibold disabled:opacity-50">
            Cancelar
          </button>
          <button onClick={saveAll} disabled={saving} data-testid="batch-confirm-btn"
            className="flex-1 py-2.5 rounded-lg text-sm font-black transition-all disabled:opacity-50"
            style={{ background: "var(--gold)", color: "#000" }}>
            {saving ? "Cerrando..." : `Cerrar ${picks.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Mobile Pick Card ────────────────────────────────────────── */
function MobilePickCard({ p, quickResult, canSetResult, del, setEditPick, setQuickResult, setFlashModal, cancelFlash, load, selected, toggleSelect }) {
  const isArchived = p.archived === true;
  const isSelected = selected?.has(p.id);
  return (
    <div
      data-testid={`admin-mobile-card-${p.id}`}
      className={`surface rounded-xl p-4 ${isArchived ? "opacity-50" : ""}`}
      style={{ border: `1px solid ${isSelected ? "var(--gold)" : isArchived ? "rgba(239,68,68,0.15)" : "rgba(255,255,255,0.06)"}` }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          {canSetResult(p.status) && (
            <input type="checkbox" data-testid={`batch-select-${p.id}`}
              checked={!!isSelected}
              onChange={() => toggleSelect(p.id)}
              className="w-4 h-4 rounded accent-yellow-500 cursor-pointer mt-1 flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm leading-snug">
            {p.home_team || "—"} <span className="text-zinc-600">vs</span> {p.away_team || "—"}
          </div>
          {(p.sport || p.league) && (
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">
              {[p.sport, p.league].filter(Boolean).join(" · ")}
            </div>
          )}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {isArchived && (
            <span className="text-[9px] font-bold text-red-400 bg-red-500/10 border border-red-500/20 px-2 py-0.5 rounded-full uppercase tracking-wider">
              Archivado
            </span>
          )}
          <StatusBadge status={p.status} />
        </div>
      </div>

      {/* Pick details */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3 text-xs">
        {p.pick_selection && <span className="text-zinc-300">{p.pick_selection}</span>}
        {p.market && <span className="text-[10px] text-zinc-600 uppercase tracking-wider">{p.market}</span>}
        {p.odds != null && <span className="font-mono font-bold text-yellow-400">{p.odds.toFixed(2)}</span>}
        <span className="text-zinc-500 font-mono">
          {p.match_date ? format(new Date(p.match_date), "dd MMM HH:mm") : "Sin fecha"}
        </span>
        {p.result_note && <span className="text-zinc-400 font-mono">{p.result_note}</span>}
      </div>

      {/* Actions */}
      {canSetResult(p.status) ? (
        quickResult?.pickId === p.id ? (
          <QuickResultRow
            pick={p}
            type={quickResult.type}
            onCancel={() => setQuickResult(null)}
            onSaved={() => { setQuickResult(null); load(); }}
          />
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setQuickResult({ pickId: p.id, type: "won" })}
                data-testid={`quick-won-${p.id}`}
                className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/25"
              >
                <CheckFat weight="fill" className="w-4 h-4" /> GANAMOS
              </button>
              <button
                onClick={() => setQuickResult({ pickId: p.id, type: "lost" })}
                data-testid={`quick-lost-${p.id}`}
                className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-bold bg-red-500/10 text-red-400 border border-red-500/25"
              >
                <XCircle weight="fill" className="w-4 h-4" /> PERDIMOS
              </button>
            </div>
            <div className="flex items-center gap-2">
              {p.is_flash ? (
                <button onClick={() => cancelFlash(p.id)} data-testid={`flash-cancel-${p.id}`}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold bg-orange-500/20 text-orange-400 border border-orange-500/40">
                  <Lightning weight="fill" className="w-3.5 h-3.5" /> FLASH ACTIVO
                </button>
              ) : (
                <button onClick={() => setFlashModal(p.id)} data-testid={`flash-btn-${p.id}`}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold bg-orange-500/10 text-orange-400 border border-orange-500/25">
                  <Lightning weight="fill" className="w-3.5 h-3.5" /> FLASH
                </button>
              )}
              <button onClick={() => setEditPick(p)} data-testid={`admin-manage-edit-${p.id}`}
                className="p-2 rounded-xl text-zinc-500 hover:text-yellow-400 hover:bg-yellow-400/10 transition-colors">
                <PencilSimple className="w-5 h-5" />
              </button>
              <button onClick={() => del(p.id)} data-testid={`admin-manage-delete-${p.id}`}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold transition-all ${
                  isArchived
                    ? "bg-red-500/5 text-red-500/50 border border-red-500/15 cursor-not-allowed"
                    : "bg-red-500/10 text-red-400 border border-red-500/25"
                }`}>
                <Trash className="w-4 h-4" />
                {isArchived ? "Ya archivado" : "Eliminar"}
              </button>
            </div>
          </div>
        )
      ) : (
        <div className="flex items-center gap-2">
          <button onClick={() => setEditPick(p)} data-testid={`admin-manage-edit-${p.id}`}
            className="p-2 rounded-xl text-zinc-500 hover:text-yellow-400 hover:bg-yellow-400/10 transition-colors">
            <PencilSimple className="w-5 h-5" />
          </button>
          <button onClick={() => del(p.id)} data-testid={`admin-manage-delete-${p.id}`}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold transition-all ${
              isArchived
                ? "bg-red-500/5 text-red-500/50 border border-red-500/15 cursor-not-allowed"
                : "bg-red-500/10 text-red-400 border border-red-500/25"
            }`}>
            <Trash className="w-4 h-4" />
            {isArchived ? "Ya archivado" : "Eliminar"}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Stat Card ───────────────────────────────────────────────── */
function StatCard({ icon, label, value, color, sub, testid }) {
  return (
    <div className="surface rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">{label}</span>
      </div>
      <div className={`font-black text-2xl ${color}`}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-600 mt-0.5">{sub}</div>}
    </div>
  );
}

/* ── Status Badge ────────────────────────────────────────────── */
function StatusBadge({ status }) {
  const styles = {
    pending: "bg-yellow-500/10 text-yellow-400 border-yellow-500/25",
    won:     "bg-emerald-500/10 text-emerald-400 border-emerald-500/25",
    lost:    "bg-red-500/10 text-red-400 border-red-500/25",
    void:    "bg-zinc-500/10 text-zinc-400 border-zinc-500/25",
    live:    "bg-blue-500/10 text-blue-400 border-blue-500/25",
  };
  const labels = { pending: "PENDIENTE", live: "EN JUEGO", won: "GANADA", lost: "PERDIDA", void: "ANULADA" };
  return <span className={`chip ${styles[status] || styles.pending} !text-[10px]`}>{labels[status] || status.toUpperCase()}</span>;
}

/* ── Quick Result Row (inline score input) ───────────────────── */
function QuickResultRow({ pick, type, onCancel, onSaved }) {
  const [note, setNote]       = useState("");
  const [saving, setSaving]   = useState(false);

  const isWon = type === "won";
  const label = isWon ? "GANAMOS" : "PERDIMOS";
  const color = isWon
    ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
    : "bg-red-500/10 border-red-500/30 text-red-300";

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.post(`/admin/picks/${pick.id}/result`, { status: type, result_note: note });
      toast.success(`${pick.home_team} vs ${pick.away_team} → ${label}`);
      onSaved();
    } catch {
      toast.error("Error al guardar resultado");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${color} w-full justify-end`}
      data-testid={`quick-result-row-${pick.id}`}>
      <span className="text-xs font-black whitespace-nowrap">{label}</span>
      <input
        autoFocus
        value={note}
        onChange={e => setNote(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") onCancel(); }}
        placeholder="Marcador (ej: 2-1)"
        className="text-xs px-2 py-1 rounded-lg bg-black/30 border border-white/10 text-white placeholder-zinc-600 outline-none w-32"
        data-testid={`quick-result-note-${pick.id}`}
      />
      <button
        onClick={save}
        disabled={saving}
        className="text-xs font-bold px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/20 transition-colors whitespace-nowrap"
        data-testid={`quick-result-save-${pick.id}`}
      >
        {saving ? "..." : "Guardar"}
      </button>
      <button
        onClick={onCancel}
        className="text-xs text-zinc-500 hover:text-white transition-colors"
        data-testid={`quick-result-cancel-${pick.id}`}
      >
        ✕
      </button>
    </div>
  );
}


/* ── Flash Pick Duration Modal ───────────────────────────────── */
function FlashModal({ pick, onConfirm, onClose }) {
  const [selected, setSelected] = useState(30);
  const [saving, setSaving]     = useState(false);

  const options = [
    { mins: 15, label: "15 min" },
    { mins: 30, label: "30 min" },
    { mins: 60, label: "1 hora" },
    { mins: 120, label: "2 horas" },
  ];

  const confirm = async () => {
    setSaving(true);
    await onConfirm(selected);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(8px)" }}
      onClick={onClose}>
      <div
        data-testid="flash-modal"
        onClick={e => e.stopPropagation()}
        className="w-full max-w-sm rounded-2xl overflow-hidden"
        style={{
          background: "#0F0A00",
          border: "1px solid rgba(249,115,22,0.4)",
          boxShadow: "0 0 60px rgba(249,115,22,0.15), 0 24px 60px rgba(0,0,0,0.7)",
        }}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between"
          style={{ borderBottom: "1px solid rgba(249,115,22,0.15)" }}>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Lightning weight="fill" className="w-5 h-5 text-orange-400" />
              <span className="font-black text-white text-lg">Activar Flash Pick</span>
            </div>
            {pick && (
              <p className="text-sm text-zinc-400">
                {pick.home_team} vs {pick.away_team}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-zinc-600 hover:text-white transition-colors mt-0.5">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Duration selector */}
        <div className="px-6 py-5">
          <p className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-4">
            ¿Cuánto tiempo estará activo?
          </p>
          <div className="grid grid-cols-2 gap-2 mb-6">
            {options.map(o => (
              <button
                key={o.mins}
                onClick={() => setSelected(o.mins)}
                data-testid={`flash-duration-${o.mins}`}
                className="py-3 rounded-xl font-black text-sm transition-all"
                style={{
                  background: selected === o.mins
                    ? "rgba(249,115,22,0.2)"
                    : "rgba(255,255,255,0.04)",
                  border: selected === o.mins
                    ? "2px solid rgba(249,115,22,0.6)"
                    : "2px solid rgba(255,255,255,0.08)",
                  color: selected === o.mins ? "#F97316" : "#71717A",
                }}
              >
                {o.label}
              </button>
            ))}
          </div>

          <div className="text-xs text-zinc-600 mb-4 text-center leading-relaxed">
            El banner aparecerá en toda la app con un contador regresivo de <span className="text-orange-400 font-bold">{selected} minutos</span>. Solo un Flash Pick puede estar activo a la vez.
          </div>

          <button
            onClick={confirm}
            disabled={saving}
            data-testid="flash-modal-confirm"
            className="w-full py-3.5 rounded-xl font-black text-sm transition-all flex items-center justify-center gap-2"
            style={{
              background: saving ? "rgba(249,115,22,0.2)" : "rgba(249,115,22,0.9)",
              color: saving ? "#F97316" : "#000",
              boxShadow: saving ? "none" : "0 4px 24px rgba(249,115,22,0.35)",
            }}
          >
            <Lightning weight="fill" className="w-4 h-4" />
            {saving ? "Activando..." : `Activar Flash por ${selected} min`}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ── Edit Pick Modal (Opción A: campos básicos) ─────────────── */
function EditPickModal({ pick, onClose, onSaved }) {
  const [form, setForm] = useState({
    home_team:      pick.home_team || "",
    away_team:      pick.away_team || "",
    league:         pick.league || "",
    market:         pick.market || "",
    pick_selection: pick.pick_selection || "",
    odds:           pick.odds ?? "",
    confidence:     pick.confidence ?? 5,
    analysis:       pick.analysis || "",
    notes:          pick.stats?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      const oddsNum = form.odds === "" ? null : parseFloat(form.odds);
      const payload = {
        home_team:      form.home_team.trim(),
        away_team:      form.away_team.trim(),
        league:         form.league.trim(),
        market:         form.market.trim(),
        pick_selection: form.pick_selection.trim(),
        odds:           oddsNum,
        confidence:     parseInt(form.confidence, 10) || 5,
        analysis:       form.analysis,
        // Preserve all existing stats fields, only override notes
        stats: { ...(pick.stats || {}), notes: form.notes },
      };
      await apiClient.patch(`/admin/picks/${pick.id}`, payload);
      toast.success("Pick actualizado correctamente");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al actualizar el pick");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-md bg-black/70"
         onClick={onClose} data-testid="edit-pick-modal">
      <div className="surface rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 relative"
           onClick={(e) => e.stopPropagation()}
           style={{ background: "#141417", border: "1px solid rgba(212,175,55,0.25)" }}>
        <button onClick={onClose}
                className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-white/10"
                data-testid="edit-pick-close">
          <X className="w-5 h-5" />
        </button>

        <div className="mb-5 pr-8">
          <div className="text-[10px] uppercase tracking-[0.2em] font-black text-yellow-400 mb-1">
            Editar Pick
          </div>
          <div className="text-display font-bold text-xl">
            {pick.home_team} <span className="text-zinc-600">vs</span> {pick.away_team}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Equipo local">
            <input value={form.home_team} onChange={e => update("home_team", e.target.value)}
                   className="input-base" data-testid="edit-home" />
          </Field>
          <Field label="Equipo visitante">
            <input value={form.away_team} onChange={e => update("away_team", e.target.value)}
                   className="input-base" data-testid="edit-away" />
          </Field>
          <Field label="Liga">
            <input value={form.league} onChange={e => update("league", e.target.value)}
                   className="input-base" data-testid="edit-league" />
          </Field>
          <Field label="Mercado">
            <input value={form.market} onChange={e => update("market", e.target.value)}
                   className="input-base" data-testid="edit-market" />
          </Field>
          <Field label="Selección" full>
            <input value={form.pick_selection} onChange={e => update("pick_selection", e.target.value)}
                   className="input-base" data-testid="edit-selection" />
          </Field>
          <Field label="Cuota">
            <input type="number" step="0.01" min="1.01" value={form.odds}
                   onChange={e => update("odds", e.target.value)}
                   className="input-base font-mono" data-testid="edit-odds" />
          </Field>
          <Field label="Confianza (1-10)">
            <input type="number" min="1" max="10" value={form.confidence}
                   onChange={e => update("confidence", e.target.value)}
                   className="input-base font-mono" data-testid="edit-confidence" />
          </Field>
          <Field label="Análisis (texto público)" full>
            <textarea value={form.analysis} onChange={e => update("analysis", e.target.value)}
                      rows={3} className="input-base resize-none" data-testid="edit-analysis" />
          </Field>
          <Field label="Notas de Ronald (efecto destacado en la tarjeta)" full hint="Visibles a los suscriptores con efecto parpadeo dorado.">
            <textarea value={form.notes} onChange={e => update("notes", e.target.value)}
                      rows={3} className="input-base resize-none" data-testid="edit-notes"
                      placeholder="Ej: 'Cuota especial, jugada de alta convicción' …" />
          </Field>
        </div>

        <div className="flex items-center gap-3 mt-6">
          <button onClick={onClose} disabled={saving}
                  className="px-5 py-2.5 rounded-lg font-bold text-sm border border-white/10 hover:bg-white/5"
                  data-testid="edit-cancel">
            Cancelar
          </button>
          <button onClick={submit} disabled={saving}
                  className="flex-1 py-2.5 rounded-lg font-black text-sm transition-all"
                  data-testid="edit-save"
                  style={{
                    background: saving ? "rgba(212,175,55,0.2)" : "#D4AF37",
                    color: saving ? "#D4AF37" : "#000",
                    boxShadow: saving ? "none" : "0 4px 18px rgba(212,175,55,0.35)",
                  }}>
            {saving ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>

        <style>{`
          .input-base {
            width: 100%; padding: 10px 14px; border-radius: 10px;
            background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
            color: #fafafa; font-size: 14px; outline: none;
            transition: border-color .15s, background .15s;
          }
          .input-base:focus { border-color: rgba(212,175,55,0.55); background: rgba(212,175,55,0.04); }
        `}</style>
      </div>
    </div>
  );
}

function Field({ label, hint, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500 mb-1.5">
        {label}
      </label>
      {children}
      {hint && <div className="text-[11px] text-zinc-600 mt-1">{hint}</div>}
    </div>
  );
}
